// src/telas/tecnicos/index.tsx

import styles from "./styles.module.css";
import { Link, useNavigate } from "react-router-dom";
import { useContext, useEffect, useState, useMemo } from "react";
import { AuthFetchJson, url } from "../../api";
import { ContextRegister } from "../../context";
import { useDialog } from "../../components/dialog";

import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from "@tanstack/react-table";

import {
  FaRegEdit,
  FaRegTrashAlt,
  FaChevronUp,
  FaChevronDown,
} from "react-icons/fa";
import { Menu } from "../menu";

type Tecnico = {
  A2_COD: string;
  A2_LOJA: string;
  A2_NOME: string;
  A2_TEL: string;
};

const columnHelper = createColumnHelper<Tecnico>();

export function Tecnicos() {
  const {
    state: { usuario },
  } = useContext(ContextRegister);

  const navigate = useNavigate();
  const dialog = useDialog();

  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);

  async function buscarTecnicos() {
    setLoading(true);
    const id = dialog.loading("Buscando técnicos...");

    try {
      const retorno: any = await AuthFetchJson(`${url}COLETAFORNECEDOR`, {
        method: "POST",
        body: JSON.stringify({
          TECFOR: "S",
          LOJA: usuario.A2_LOJA,
          PARCEIRO: usuario.A2_COD,
        }),
      });

      dialog.close(id);

      if (retorno.sucesso !== "T") {
        dialog.error("Erro ao buscar técnicos", retorno.mensagem);
        return;
      }

      setTecnicos(retorno.dados ?? []);
    } catch (error) {
      dialog.close(id);
      dialog.error("Erro ao buscar técnicos", String(error));
    } finally {
      setLoading(false);
    }
  }

  async function removerTecnico(tecnico: Tecnico) {
    const confirmado = await dialog.confirm({
      title: "Remover técnico?",
      description: `Deseja realmente remover o vínculo com o técnico "${tecnico.A2_NOME.trim()}"?`,
      confirmLabel: "Sim, remover",
      cancelLabel: "Cancelar",
      danger: true,
    });

    if (!confirmado) return;

    const id = dialog.loading("Removendo técnico...");

    try {
      const retorno: any = await AuthFetchJson(`${url}REMOVETECNICO`, {
        method: "POST",
        body: JSON.stringify({
          PARCEIRO: usuario.A2_COD,
          LOJA: usuario.A2_LOJA,
          TECNICO: tecnico.A2_COD,
          LOJTEC: tecnico.A2_LOJA,
        }),
      });

      dialog.close(id);

      if (retorno.sucesso !== "T") {
        dialog.error("Erro ao remover técnico", retorno.mensagem);
        return;
      }

      dialog.success(
        "Técnico removido!",
        "O vínculo foi desfeito com sucesso.",
      );

      setTecnicos((prev) =>
        prev.filter(
          (t) =>
            !(t.A2_COD === tecnico.A2_COD && t.A2_LOJA === tecnico.A2_LOJA),
        ),
      );
    } catch (error) {
      dialog.close(id);
      dialog.error("Erro ao remover técnico", String(error));
    }
  }

  const columns = useMemo(
    () => [
      columnHelper.accessor("A2_COD", {
        header: "Código",
        cell: (info) => info.getValue().trim(),
        size: 100,
      }),
      columnHelper.accessor("A2_NOME", {
        header: "Nome",
        cell: (info) => (
          <span style={{ display: "block", textAlign: "left" }}>
            {info.getValue().trim()}
          </span>
        ),
      }),
      columnHelper.accessor("A2_TEL", {
        header: "Telefone",
        cell: (info) => {
          const tel = info.getValue().trim().replace(/\D/g, "");

          if (tel.length === 11) {
            return tel.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
          }
          if (tel.length === 10) {
            return tel.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
          }

          return info.getValue().trim(); // fallback se o número vier fora do padrão
        },
        size: 150,
      }),
      columnHelper.display({
        id: "acoes",
        header: "Ações",
        size: 100,
        cell: ({ row }) => (
          <div className={styles.actionIcons}>
            <Link
              to={"/editar-tecnico/" + btoa(row.original.A2_COD)}
              title="Editar técnico"
            >
              <FaRegEdit />
            </Link>
            <button
              className={styles.btnRemove}
              title="Remover técnico desta empresa"
              onClick={() => removerTecnico(row.original)}
            >
              <FaRegTrashAlt />
            </button>
          </div>
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const table = useReactTable({
    data: tecnicos,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  useEffect(() => {
    buscarTecnicos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.container}>
      <Menu />

      <div className={styles.content}>
        {/* botão cadastrar */}
        <div className={styles.actionContainer}>
          <h2>Técnicos</h2>
          <button onClick={() => navigate("/cadastro-tecnico")}>
            Cadastrar
          </button>
        </div>

        {/* buscar técnico */}
        <div className={styles.tableToolbar}>
          <input
            className={styles.searchInput}
            placeholder="Buscar técnico..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
          />
          <span className={styles.countLabel}>
            {table.getFilteredRowModel().rows.length} registro(s)
          </span>
        </div>

        {loading ? (
          <p className={styles.loadingText}>Carregando...</p>
        ) : (
          <table className={styles.table}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{ width: header.getSize() }}
                      onClick={header.column.getToggleSortingHandler()}
                      className={
                        header.column.getCanSort() ? styles.sortable : ""
                      }
                    >
                      <span className={styles.thInner}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {header.column.getIsSorted() === "asc" && (
                          <FaChevronUp className={styles.sortIcon} />
                        )}
                        {header.column.getIsSorted() === "desc" && (
                          <FaChevronDown className={styles.sortIcon} />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className={styles.emptyRow}>
                    Nenhum técnico encontrado.
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
